import type { Actuals } from '@quicktide/shared'

/** Talks to /api/v1 with the device token; registers the device on first use */
const TOKEN_KEY = 'quicktide.device'
import { APP_VERSION } from './update'

let token: string | null = localStorage.getItem(TOKEN_KEY)
let registering: Promise<string> | null = null

async function register(): Promise<string> {
  const res = await fetch('/api/v1/auth/device', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ platform: navigator.platform || 'web', appVersion: APP_VERSION })
  })
  if (!res.ok) throw new Error(`Device registration failed: ${res.status}`)
  const data = await res.json() as { token: string }
  localStorage.setItem(TOKEN_KEY, data.token)
  token = data.token
  return data.token
}

async function ensureToken(): Promise<string> {
  if (token) return token
  registering ??= register().finally(() => { registering = null })
  return registering
}

export async function apiFetch(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const t = await ensureToken()
  // the version lets the server count which versions are still out there (stats.ts)
  const res = await fetch(`/api/v1${path}`, { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${t}`, 'X-QuickTide-Version': APP_VERSION } })
  if (res.status === 401 && retry) {
    // token unknown to the server (data wiped, or a new server): register again once
    localStorage.removeItem(TOKEN_KEY)
    token = null
    return apiFetch(path, init, false)
  }
  return res
}

export interface ManifestStation {
  id: string
  name: string
  source: 'rws' | 'bsh'
  area: 'NL' | 'DE'
  lat?: number
  lon?: number
  altRef?: number
  altRefName?: string
  springLagDays?: number
}

export interface ManifestSublocation {
  id: string
  name: string
  station: string
  kind: 'hwlw' | 'timeshift'
  hwLater2: number
  hwLater8: number
  lwLater2: number
  lwLater8: number
  hwHigherSpring: number
  hwHigherNeap: number
  lwHigherSpring: number
  lwHigherNeap: number
  lat?: number
  lon?: number
  area?: string
  source?: string
  note?: string
}

export interface ManifestShallow {
  id: string
  name: string
  station: string
  /** Set when the shallow hangs off a derived place rather than the station itself */
  via?: string
  hwOffsetMinutes: number
  lwOffsetMinutes: number
  depth: number
  lat?: number
  lon?: number
  surveyed?: string
  source?: string
  note?: string
  warning?: string
}

export interface ManifestBridge {
  id: string
  name: string
  station: string
  via?: string
  hwOffsetMinutes: number
  lwOffsetMinutes: number
  /** Clearance height in cm above NAP */
  height: number
  lat?: number
  lon?: number
  surveyed?: string
  source?: string
  note?: string
}

export interface ManifestSeries {
  station: string
  year: number
  kind: 'levels' | 'extrema'
  size: number
  hash: string
  status: string
}

/** What this device may read, as the server sees it */
export interface Licence {
  state: 'demo' | 'granted' | 'expired'
  /** Readable range in ms since the epoch; null at either end means unbounded */
  from: number | null
  to: number | null
  demoDays: number
  /** The account types that may activate just now; null when every qualified account may */
  openTo?: string[] | null
  reason?: 'unknown' | 'expired'
  account?: {
    type: string
    expires: string | null
    displayName?: string
    registrations: number
    maxRegistrations: number
  }
}

export interface Manifest {
  generatedAt: string
  /** Git revision of the server build */
  version?: string
  licence?: Licence
  stations: ManifestStation[]
  sublocations: ManifestSublocation[]
  shallows: ManifestShallow[]
  bridges: ManifestBridge[]
  series: ManifestSeries[]
}

export async function getManifest(): Promise<Manifest> {
  const res = await apiFetch('/manifest', { cache: 'no-store' })
  if (!res.ok) throw new Error(`Manifest: HTTP ${res.status}`)
  return res.json()
}

export interface MeteoArea {
  id: string
  name: string
  html: string
  sources: string[]
  issued: string | null
}

export interface Meteo {
  areas: MeteoArea[]
  fetchedAt: string
  failed: string[]
}

/** The weather bulletins, as this server last fetched them from KNMI and DWD */
export async function getMeteo(): Promise<Meteo> {
  const res = await apiFetch('/meteo', { cache: 'no-store' })
  if (!res.ok) throw new Error(`Meteo: HTTP ${res.status}`)
  return res.json()
}

export interface ActualsSnapshot {
  generatedAt: string
  stations: Record<string, Actuals>
}

/**
 * Measured and forecast levels of every station, about 65 kB gzipped. `etag` is the tag of the
 * copy already stored; null comes back when the server answers 304, meaning that copy is current.
 */
export async function getActuals(etag: string | null): Promise<{ snapshot: ActualsSnapshot; etag: string | null } | null> {
  const res = await apiFetch('/actuals', { cache: 'no-store', ...(etag ? { headers: { 'If-None-Match': etag } } : {}) })
  if (res.status === 304) return null
  if (!res.ok) throw new Error(`Actuals: HTTP ${res.status}`)
  return { snapshot: await res.json(), etag: res.headers.get('ETag') }
}

/** Is this the copy the manifest promised? The manifest's hash is the first half of a sha256 */
async function asPromised(bytes: Uint8Array, expect: { size: number; hash: string }): Promise<boolean> {
  if (bytes.byteLength !== expect.size) return false
  const digest = await crypto.subtle.digest('SHA-256', bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer)
  const hex = [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('')
  return hex.startsWith(expect.hash)
}

/**
 * One series, as the manifest promised it. The same URL serves different bytes to different
 * licences -- a demo device gets a few days cut from the year -- and the browser's cache does
 * not know that: a device that synced before it was activated was handed its old cut back
 * for a day, under the new hash, and then kept it for good. So the hash rides in the URL,
 * which makes a different copy a different address, and what arrives is measured before it
 * is kept: a copy that is not the one promised is fetched past the cache once, then refused.
 */
export async function getSeries(year: number, station: string, kind: 'levels' | 'extrema', expect: { size: number; hash: string }): Promise<Uint8Array> {
  const path = `/series/${year}/${encodeURIComponent(station)}/${kind}.bin?v=${expect.hash}`
  let res = await apiFetch(path)
  if (!res.ok) throw new Error(`Series ${station} ${year} ${kind}: HTTP ${res.status}`)
  let bytes = new Uint8Array(await res.arrayBuffer())
  if (await asPromised(bytes, expect)) return bytes
  res = await apiFetch(path, { cache: 'reload' })
  if (!res.ok) throw new Error(`Series ${station} ${year} ${kind}: HTTP ${res.status}`)
  bytes = new Uint8Array(await res.arrayBuffer())
  if (!(await asPromised(bytes, expect))) throw new Error(`Series ${station} ${year} ${kind}: not the copy the manifest promised`)
  return bytes
}


/* ---------- the administrator's message ---------- */

export interface Notice {
  id: string
  text: string
  audience: 'all' | 'registered'
  until: string | null
  setAt: string
}

/** The message for this device, if the administrator has set one; null when not, or offline */
export async function getNotice(): Promise<Notice | null> {
  try {
    const res = await apiFetch('/notice', { cache: 'no-store' })
    if (!res.ok) return null
    return ((await res.json()) as { notice: Notice | null }).notice
  } catch {
    return null
  }
}

/* ---------- registration ---------- */

export type RequestOutcome = 'sent' | 'unknown' | 'closed' | 'expired' | 'limit' | 'invalid' | 'mail-failed' | 'failed'

export interface RequestResult {
  status: RequestOutcome
  /** Where an unknown or expired address is sent to put that right */
  donateUrl?: string
  expires?: string | null
  maxRegistrations?: number
  registrations?: number
  /** With `closed`: the account types that may activate just now */
  openTo?: string[]
  /** False when the server has no mailer configured and only logged the link */
  mailed?: boolean
  /**
   * The account already has its maximum of devices. The link is sent anyway: the page it
   * opens is the only place this device can see them and give one back.
   */
  atLimit?: boolean
}

export interface Registration {
  id: string
  name: string | null
  platform: string | null
  appVersion: string | null
  boundAt: string
  lastSeenAt: string
  current: boolean
}

/** What this device may read; the app asks on every start and after every registration step */
export async function getLicence(): Promise<Licence> {
  const res = await apiFetch('/auth/state', { cache: 'no-store' })
  if (!res.ok) throw new Error(`Licence: HTTP ${res.status}`)
  return res.json()
}

/** Ask for the activation mail. Everything but a network failure comes back as a status. */
export async function requestAccess(email: string, deviceName: string, locale: string): Promise<RequestResult> {
  const res = await apiFetch('/auth/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, deviceName, locale })
  })
  try {
    const body = await res.json() as RequestResult
    if (body.status) return body
  } catch {
    // an error page rather than an answer
  }
  return { status: 'failed' }
}

/** What became of the last activation mail, as far as the server has heard back */
export interface MailState {
  state: 'none' | 'sent' | 'delayed' | 'bounced' | 'failed'
  reason?: 'full' | 'unknown' | 'rejected' | 'delayed'
  sentAt?: string
}

export async function getMailState(): Promise<MailState | null> {
  try {
    const res = await apiFetch('/auth/mail', { cache: 'no-store' })
    return res.ok ? await res.json() as MailState : null
  } catch {
    return null
  }
}

export async function getRegistrations(): Promise<Registration[]> {
  const res = await apiFetch('/auth/registrations', { cache: 'no-store' })
  if (!res.ok) return []
  return (await res.json()).registrations as Registration[]
}

/** Give a device back: another one is forgotten, this one keeps its token and falls to demo */
export async function revokeRegistration(id: string): Promise<Licence | null> {
  const res = await apiFetch(`/auth/registrations/${encodeURIComponent(id)}`, { method: 'DELETE' })
  return res.ok ? (await res.json()).licence as Licence : null
}

export async function cancelRegistration(): Promise<Licence | null> {
  const res = await apiFetch('/auth/cancel', { method: 'POST' })
  return res.ok ? (await res.json()).licence as Licence : null
}
